/**
 * SchemaGrantsPanel
 *
 * Single component for displaying and editing schema_grants (field-level +
 * predicate filtering) across all permission surfaces.
 *
 * Modes:
 *   'consent'     — app/user/follow authorization screen. Shows a plain-English
 *                   summary of what the app declared. Customize button reveals
 *                   field pickers. Default = full DLR grants (user narrows).
 *   'receipt'     — read-only display in EP cards and dashboard.
 *   'edit'        — same as consent but pre-loaded from an existing EP.
 *                   Includes Save button that calls onSave().
 *   'dlr-builder' — developer form inside DeveloperDashboard. Full editable
 *                   interface: add schemas, pick fields, build filter conditions.
 *
 * Props:
 *   mode         {string}    'consent' | 'receipt' | 'edit' | 'dlr-builder'
 *   schemaGrants {object}    Controlled current value ({ uri: { fields, filter } })
 *   dlrGrants    {object}    DLR ceiling (null = no ceiling, person-to-person)
 *   onChange     {function}  Called with updated grants object on every change
 *   onSave       {function}  Called with grants when Save is clicked (edit mode)
 *   onCancel     {function}  Called when Cancel is clicked (edit mode)
 *   schemas      {Array}     Available schemas [{ id, uri, name }] (dlr-builder)
 *   collapsed    {bool}      Start collapsed (consent/edit/dlr-builder)
 *   saving       {bool}      Show saving spinner (edit mode)
 *   saveError    {string}    Show error message (edit mode)
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Check, SlidersHorizontal } from 'lucide-react';
import api from '@paradigm/api';
import './SchemaGrantsPanel.css';
import FilterBuilder from './FilterBuilder';
import {
  grantToEnglish,
  grantsToSummary,
  grantsToProseSummary,
  schemaNameToLabel,
  filterToConditions,
  predicateValueToEnglish,
  buildEffectiveGrants,
  isNarrowerThanDlr,
} from './schemaGrantsUtils';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Render a filter object as readable badges */
function FilterBadges({ filter, readOnly = true }) {
  if (!filter || Object.keys(filter).length === 0) return null;
  const conditions = filterToConditions(filter);
  return (
    <div className="sg-filter-badges">
      {conditions.map((cond, i) => (
        <span key={i} className="sg-filter-badge">
          <span className="sg-filter-badge__val">{cond}</span>
        </span>
      ))}
    </div>
  );
}

/** A single field chip (toggleable or readonly) */
function FieldChip({ field, selected, onClick, readOnly, disabled }) {
  let cls = 'sg-field-chip';
  if (readOnly) cls += ' sg-field-chip--readonly';
  else if (selected) cls += ' sg-field-chip--selected';
  if (disabled) cls += ' sg-field-chip--disabled';

  return (
    <button
      type="button"
      className={`btn ${cls}`}
      onClick={!readOnly && !disabled ? onClick : undefined}
      title={readOnly ? field : (selected ? `Deselect ${field}` : `Select ${field}`)}
    >
      {field}
    </button>
  );
}


/**
 * Field chip list for dlr-builder — with add/remove.
 * When filterableFields is provided (from schema json_schema), shows clickable
 * chips for each field so users don't have to type field names.
 */
function DlrFieldPicker({ fields, onChange, filterableFields }) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const f = draft.trim();
    if (!f || fields.includes(f)) return;
    onChange([...fields, f]);
    setDraft('');
  };

  const toggle = (field) => {
    if (fields.includes(field)) {
      onChange(fields.filter(f => f !== field));
    } else {
      onChange([...fields, field]);
    }
  };

  const remove = (field) => onChange(fields.filter(f => f !== field));

  return (
    <div className="sg-fields-row">
      <span className="sg-fields-row__label">Fields</span>
      <div className="sg-fields-chips">
        {filterableFields && filterableFields.length > 0 ? (
          // Schema-aware: show all schema fields as toggleable chips
          filterableFields.filter(f => !f.encrypted).map(f => {
            const selected = fields.includes(f.key);
            return (
              <button
                key={f.key}
                type="button"
                className={`btn sg-field-chip ${selected ? 'sg-field-chip--selected' : ''}`}
                onClick={() => toggle(f.key)}
                title={selected ? `Deselect ${f.key}` : `Select ${f.key}`}
              >
                {f.key}
              </button>
            );
          })
        ) : (
          // Free-text fallback
          <>
            {fields.map(field => (
              <button
                key={field}
                type="button"
                className="btn sg-field-chip sg-field-chip--selected"
                onClick={() => remove(field)}
                title={`Remove ${field}`}
              >
                {field} ×
              </button>
            ))}
            <input
              className="sg-field-chip sg-field-chip--add"
              style={{
                border: '1px dashed var(--primary)',
                background: 'transparent',
                color: 'var(--primary)',
                cursor: 'text',
                outline: 'none',
                width: draft ? Math.max(80, draft.length * 8 + 20) : 100,
                fontFamily: "ui-monospace, 'SF Mono', 'Fira Code', Consolas, monospace",
                fontSize: 12,
                padding: '3px 9px',
              }}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="+ field"
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
              onBlur={add}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Receipt mode
// ---------------------------------------------------------------------------

function ReceiptView({ schemaGrants }) {
  const items = grantsToSummary(schemaGrants);
  if (items.length === 0) return null;

  return (
    <div className="sg-receipt">
      {items.map(g => (
        <div key={g.schemaName} className="sg-receipt__row">
          <span className="sg-receipt__uri">{g.schemaName}</span>
          <div className="sg-receipt__content">
            {g.hasFields ? (
              g.fields.map(f => (
                <FieldChip key={f} field={f} readOnly selected />
              ))
            ) : (
              <span className="sg-receipt__full-access">all fields</span>
            )}
            {g.hasFilter && (
              <FilterBadges filter={g.filter} readOnly />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Consent mode — per-schema grant card (schema-aware, editable filter)
// ---------------------------------------------------------------------------

function ConsentGrantCard({ schemaName, dlrGrant, userGrant, onChange }) {
  const [schemaDetail, setSchemaDetail] = useState(null);

  useEffect(() => {
    if (!schemaName) return;
    api.request(`/schemas/name/${encodeURIComponent(schemaName)}?resolve=true`)
      .then(s => setSchemaDetail(s))
      .catch(() => setSchemaDetail(null));
  }, [schemaName]);

  const filterableFields = useMemo(() => {
    if (!schemaDetail?.json_schema?.properties) return null;
    const encrypted = new Set(schemaDetail.encrypted_fields || []);
    return Object.keys(schemaDetail.json_schema.properties)
      .filter(key => !encrypted.has(key))
      .map(key => ({ key, ...schemaDetail.json_schema.properties[key] }));
  }, [schemaDetail]);

  const dlrFields = dlrGrant?.fields || [];
  const dlrFilter = dlrGrant?.filter;
  const userFields = userGrant?.fields;
  const userFilter = userGrant?.filter;

  // Available fields: DLR-declared if the app specified them, else all schema fields
  const availableFields = dlrFields.length > 0
    ? dlrFields
    : (filterableFields?.map(f => f.key) || []);

  const activeFields = (userFields && userFields.length > 0) ? userFields : availableFields;
  const isCustom = userFields && userFields.length > 0 && availableFields.length > 0
    && userFields.length < availableFields.length;
  const [fieldMode, setFieldMode] = useState(isCustom ? 'custom' : 'all');
  const [showFilter, setShowFilter] = useState(!!(userFilter));

  const g = grantToEnglish(schemaName, userGrant || dlrGrant || {});

  const handleFilterChange = (newFilter) => {
    const next = { ...(userGrant || {}) };
    if (newFilter) next.filter = newFilter; else delete next.filter;
    onChange(schemaName, Object.keys(next).length > 0 ? next : null);
  };

  const switchFieldMode = (m) => {
    setFieldMode(m);
    if (m === 'all') onChange(schemaName, null);
  };

  const toggleField = (field) => {
    const current = (userFields && userFields.length > 0) ? userFields : availableFields;
    const next = current.includes(field) ? current.filter(f => f !== field) : [...current, field];
    if (next.length === 0) return;
    onChange(schemaName, { ...(userGrant || {}), fields: next });
  };

  const leftBorder = dlrFilter ? 'var(--info, #2d6a8a)' : 'var(--primary)';

  return (
    <div className="sg-grant-card" style={{ borderLeftColor: leftBorder }}>
      <div className="sg-grant-card__header">
        <code className="sg-grant-card__uri">{schemaName}</code>
        <span className="sg-grant-card__detail">{g.detail}</span>
      </div>

      {/* Field selection: shown when DLR declares fields OR schema is loaded */}
      {availableFields.length > 0 && (
        <div className="sg-preset-row">
          <span className="sg-preset-row__label">Fields</span>
          <div className="sg-preset-options">
            <button
              type="button"
              className={`btn sg-preset-option ${fieldMode === 'all' ? 'sg-preset-option--active' : ''}`}
              onClick={() => switchFieldMode('all')}
            >
              <span className="sg-preset-option__radio" />
              <span className="sg-preset-option__text">
                <span className="sg-preset-option__name">All ({availableFields.length})</span>
              </span>
            </button>
            <button
              type="button"
              className={`btn sg-preset-option ${fieldMode === 'custom' ? 'sg-preset-option--active' : ''}`}
              onClick={() => switchFieldMode('custom')}
            >
              <span className="sg-preset-option__radio" />
              <span className="sg-preset-option__text">
                <span className="sg-preset-option__name">Choose</span>
              </span>
            </button>
          </div>
          {fieldMode === 'custom' && (
            <div className="sg-fields-chips" style={{ marginTop: 4 }}>
              {availableFields.map(field => (
                <FieldChip
                  key={field}
                  field={field}
                  selected={activeFields.includes(field)}
                  onClick={() => toggleField(field)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* DLR filter — informational */}
      {dlrFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', flexShrink: 0 }}>App filter</span>
          <FilterBadges filter={dlrFilter} readOnly />
        </div>
      )}

      {/* User's own row filter — editable */}
      <button className="btn"
        type="button"
        style={{
          fontSize: 11,
          padding: '2px 8px',
          borderRadius: 4,
          border: '1px solid var(--border-color)',
          background: showFilter ? 'var(--info-light, #e4f0f8)' : 'transparent',
          color: showFilter ? 'var(--info, #2d6a8a)' : 'var(--text-muted)',
          cursor: 'pointer',
          transition: 'all 0.12s',
          alignSelf: 'flex-start',
        }}
        onClick={() => {
          if (showFilter) { setShowFilter(false); handleFilterChange(null); }
          else setShowFilter(true);
        }}
      >
        {showFilter ? (
          <><Check size={10} strokeWidth={2.5} style={{ verticalAlign: -1, marginRight: 3 }} />Row filter</>
        ) : '+ Row filter'}
      </button>

      {showFilter && (
        <FilterBuilder
          filter={userFilter || null}
          onChange={handleFilterChange}
          readOnly={false}
          filterableFields={filterableFields}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DLR-builder mode — per-schema grant card (fully editable)
// ---------------------------------------------------------------------------

export function DlrGrantCard({ schemaName, grant, onChange, onRemove }) {
  const [showFilter, setShowFilter] = useState(!!(grant?.filter));
  const [schemaDetail, setSchemaDetail] = useState(null);

  // Fetch schema json_schema for type-aware field + filter UI
  useEffect(() => {
    if (!schemaName) return;
    api.request(`/schemas/name/${encodeURIComponent(schemaName)}?resolve=true`)
      .then(s => setSchemaDetail(s))
      .catch(() => setSchemaDetail(null));
  }, [schemaName]);

  const filterableFields = useMemo(() => {
    if (!schemaDetail?.json_schema?.properties) return null;
    const encrypted = new Set(schemaDetail.encrypted_fields || []);
    return Object.keys(schemaDetail.json_schema.properties).map(key => {
      const def = schemaDetail.json_schema.properties[key];
      return {
        key,
        encrypted: encrypted.has(key),
        type: def.type,
        format: def.format,
        enum: def.enum,
      };
    });
  }, [schemaDetail]);

  const fields = grant?.fields || [];
  const filter = grant?.filter || null;

  const handleFieldsChange = (newFields) => {
    onChange(schemaName, { ...grant, fields: newFields });
  };

  const handleFilterChange = (newFilter) => {
    onChange(schemaName, { ...grant, filter: newFilter });
  };

  return (
    <div className="sg-grant-card">
      <DlrFieldPicker
        fields={fields}
        onChange={handleFieldsChange}
        filterableFields={filterableFields}
      />

      {/* Filter toggle */}
      <button className="btn"
        type="button"
        style={{
          fontSize: 11,
          padding: '2px 8px',
          borderRadius: 4,
          border: '1px solid var(--border-color)',
          background: showFilter ? 'var(--info-light, #e4f0f8)' : 'transparent',
          color: showFilter ? 'var(--info, #2d6a8a)' : 'var(--text-muted)',
          cursor: 'pointer',
          transition: 'all 0.12s',
          alignSelf: 'flex-start',
        }}
        onClick={() => {
          if (showFilter) { setShowFilter(false); handleFilterChange(null); }
          else setShowFilter(true);
        }}
      >
        {showFilter ? (
          <><Check size={10} strokeWidth={2.5} style={{ verticalAlign: -1, marginRight: 3 }} />Row filter</>
        ) : '+ Row filter'}
      </button>

      {showFilter && (
        <FilterBuilder
          filter={filter}
          onChange={handleFilterChange}
          readOnly={false}
          filterableFields={filterableFields}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function SchemaGrantsPanel({
  mode = 'consent',
  schemaGrants = null,
  dlrGrants = null,
  onChange,
  onSave,
  onCancel,
  schemas = [],
  collapsed: initialCollapsed = true,
  saving = false,
  saveError = null,
}) {
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [addUri, setAddUri] = useState('');

  // ── Derived values ──────────────────────────────────────────────────────

  const hasGrants = schemaGrants && Object.keys(schemaGrants).length > 0;
  const hasDlr = dlrGrants && Object.keys(dlrGrants).length > 0;
  const narrowed = isNarrowerThanDlr(schemaGrants, dlrGrants);

  const summaryText = useMemo(() => {
    if (mode === 'dlr-builder') {
      if (!hasGrants) return 'No field restrictions — full access to all fields';
      return grantsToProseSummary(schemaGrants);
    }
    if (hasDlr) {
      return grantsToProseSummary(dlrGrants);
    }
    if (hasGrants) {
      return grantsToProseSummary(schemaGrants);
    }
    return 'Full access to all fields';
  }, [schemaGrants, dlrGrants, mode, hasGrants, hasDlr]);

  const badgeLabel = useMemo(() => {
    if (mode === 'dlr-builder') {
      return hasGrants
        ? `${Object.keys(schemaGrants).length} schema${Object.keys(schemaGrants).length !== 1 ? 's' : ''}`
        : null;
    }
    if (narrowed) return 'Customized';
    if (hasDlr || hasGrants) return `${Object.keys(dlrGrants || schemaGrants || {}).length} schema${Object.keys(dlrGrants || schemaGrants || {}).length !== 1 ? 's' : ''}`;
    return null;
  }, [mode, hasGrants, hasDlr, narrowed, schemaGrants, dlrGrants]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleGrantChange = (schemaName, grantOrNull) => {
    const next = { ...(schemaGrants || {}) };
    if (grantOrNull === null) {
      delete next[schemaName];
    } else {
      next[schemaName] = grantOrNull;
    }
    onChange && onChange(Object.keys(next).length > 0 ? next : null);
  };

  const handleRemoveUri = (uri) => {
    const next = { ...(schemaGrants || {}) };
    delete next[uri];
    onChange && onChange(Object.keys(next).length > 0 ? next : null);
  };

  const handleAddUri = () => {
    if (!addUri) return;
    const next = { ...(schemaGrants || {}), [addUri]: {} };
    onChange && onChange(next);
    setAddUri('');
  };

  // ── Receipt mode — no header/toggle needed ───────────────────────────────

  if (mode === 'receipt') {
    if (!hasGrants) return null;
    return (
      <div style={{ marginTop: 2 }}>
        <ReceiptView schemaGrants={schemaGrants} />
      </div>
    );
  }

  // ── Shared header ────────────────────────────────────────────────────────

  const isConsent = mode === 'consent' || mode === 'edit';
  const title = mode === 'dlr-builder' ? 'Field-level restrictions' : 'Field-level access';
  const icon = <SlidersHorizontal size={15} strokeWidth={1.75} />;
  const toggleLabel = collapsed
    ? (mode === 'dlr-builder' ? 'Configure ▾' : 'Customize ▾')
    : '▴';

  // In consent mode with no DLR and no user grants, don't show panel at all
  if (isConsent && !hasDlr && !hasGrants) return null;

  // ── Active grant URIs to render ──────────────────────────────────────────

  // For consent/edit: use DLR URIs (the set the app declared)
  // For dlr-builder: use user-provided URIs
  const activeUris = mode === 'dlr-builder'
    ? Object.keys(schemaGrants || {})
    : Object.keys(dlrGrants || schemaGrants || {});

  return (
    <div className={`sg-panel ${!collapsed ? 'sg-panel--active' : ''}`}>
      {/* Header / toggle */}
      <div className="sg-panel__header" onClick={() => setCollapsed(c => !c)}>
        <div className="sg-panel__header-left">
          <span className="sg-panel__icon">{icon}</span>
          <div style={{ minWidth: 0 }}>
            <span className="sg-panel__title">{title}</span>
            {summaryText && (
              <span className="sg-panel__subtitle"> &nbsp;·&nbsp; {summaryText}</span>
            )}
          </div>
        </div>
        <div className="sg-panel__header-right">
          {badgeLabel && (
            <span className={`sg-panel__badge ${narrowed ? '' : 'sg-panel__badge--neutral'}`}>
              {narrowed ? 'Customized' : badgeLabel}
            </span>
          )}
          <span className={`sg-panel__toggle-icon ${!collapsed ? 'sg-panel__toggle-icon--open' : ''}`}>
            {toggleLabel}
          </span>
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="sg-panel__body">
          {/* Consent / Edit mode */}
          {isConsent && (
            <>
              {activeUris.length === 0 ? (
                <div className="sg-empty">
                  No field-level restrictions declared by this app.
                </div>
              ) : (
                activeUris.map(uri => (
                  // In edit mode with no DLR ceiling (EP owner editing own grants), use the
                  // full DlrGrantCard so field chips + type-aware filter are available.
                  mode === 'edit' && !dlrGrants ? (
                    <DlrGrantCard
                      key={uri}
                      schemaName={uri}
                      grant={schemaGrants?.[uri] || {}}
                      onChange={handleGrantChange}
                      onRemove={handleRemoveUri}
                    />
                  ) : (
                    <ConsentGrantCard
                      key={uri}
                      schemaName={uri}
                      dlrGrant={dlrGrants?.[uri] || null}
                      userGrant={schemaGrants?.[uri] || null}
                      onChange={handleGrantChange}
                    />
                  )
                ))
              )}

              {mode === 'edit' && (
                <div className="sg-edit-footer">
                  {saveError && <span className="sg-error">{saveError}</span>}
                  {saving && <span className="sg-saving">Saving…</span>}
                  <button className="btn"
                    type="button"
                    style={{ fontSize: 12, padding: '5px 14px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                    onClick={onCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button className="btn"
                    type="button"
                    style={{ fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 6, border: '1px solid var(--primary)', background: 'var(--primary)', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
                    onClick={() => onSave && onSave(schemaGrants)}
                    disabled={saving}
                  >
                    Save restrictions
                  </button>
                </div>
              )}
            </>
          )}

          {/* DLR builder mode */}
          {mode === 'dlr-builder' && (
            <>
              {activeUris.length === 0 ? (
                schemas.length > 0 ? null : (
                  <div className="sg-empty">
                    No field restrictions yet. Add a schema below to restrict which fields are accessible.
                  </div>
                )
              ) : (
                activeUris.map(uri => (
                  <DlrGrantCard
                    key={uri}
                    schemaName={uri}
                    grant={schemaGrants?.[uri] || {}}
                    onChange={handleGrantChange}
                    onRemove={handleRemoveUri}
                  />
                ))
              )}

              {/* Add schema URI — hidden when all available schemas already have cards */}
              {(schemas.length === 0 || schemas.some(s => !activeUris.includes(s.schema_name || s.uri))) && (
              <div className="sg-add-row">
                {schemas.length > 0 ? (
                  <select
                    className="sg-add-row__select"
                    value={addUri}
                    onChange={e => setAddUri(e.target.value)}
                  >
                    <option value="">Select a schema…</option>
                    {schemas
                      .filter(s => !activeUris.includes(s.schema_name || s.uri))
                      .map(s => {
                        const uri = s.schema_name || s.uri;
                        return (
                          <option key={s.id || uri} value={uri}>
                            {uri}
                          </option>
                        );
                      })}
                  </select>
                ) : (
                  <input
                    className="sg-add-row__select"
                    value={addUri}
                    onChange={e => setAddUri(e.target.value)}
                    placeholder="Schema Name (e.g. health-metric)"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddUri(); } }}
                  />
                )}
                <button
                  type="button"
                  className="btn sg-add-row__btn"
                  onClick={handleAddUri}
                  disabled={!addUri}
                >
                  + Add schema restriction
                </button>
              </div>
              )}

              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                Optionally restrict access to specific fields within each schema.
                Omitting a schema means no field-level restriction applies to it — full access.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
